import Auth from '@/packages/Auth';
import { HttpClient, IHttpClient } from '@/packages/http/client';
import { AxiosPromise, AxiosRequestConfig } from 'axios';
import router from '@/router';
import {
  AllReqPageName,
  PendingReqPageName
} from '../../modules/amDashboard/routes';
import { AuthService } from '@/modules/auth/services';
import { LOGIN_ROUTE, LOGIN_ROUTES } from '@/utils/constants';
import { REFRESH_TOKEN } from '@/modules/auth/endpoints';

/**
 * --------------------------------------------------
 *	HTTP Service
 * --------------------------------------------------
 **/
export class HttpService {
  /**
   * Http Client Constructor.
   * @param {IHttpClient} httpClient HTTP Service
   */
  constructor(protected httpClient: IHttpClient) {
    this.prepareClient(httpClient);
  }

  /**
   * Send a Request.
   * @param {HTTPRequestConfig} config Request Configuration.
   * @returns {HTTPPromise} Response Promise.
   */
  request<T = any>(config: AxiosRequestConfig): HTTPPromise<T> {
    return this.httpClient.request(config);
  }

  /**
   * Send a Request.
   * @param {HTTPRequestConfig} config Request Configuration.
   * @returns {HTTPPromise} Response Promise.
   */
  get<T = any>(url: string, config?: AxiosRequestConfig): HTTPPromise<T> {
    return this.httpClient.get(url, config);
  }

  /**
   * Send a Request.
   * @param {HTTPRequestConfig} config Request Configuration.
   * @returns {HTTPPromise} Response Promise.
   */
  delete(url: string, config?: AxiosRequestConfig): HTTPPromise {
    return this.httpClient.delete(url, config);
  }

  /**
   * Send a Request.
   * @param {HTTPRequestConfig} config Request Configuration.
   * @returns {HTTPPromise} Response Promise.
   */
  head(url: string, config?: AxiosRequestConfig): HTTPPromise {
    return this.httpClient.head(url, config);
  }

  /**
   * Send a Request.
   * @param {HTTPRequestConfig} config Request Configuration.
   * @returns {HTTPPromise} Response Promise.
   */
  post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): HTTPPromise<T> {
    return this.httpClient.post(url, data, config);
  }

  /**
   * Send a Request.
   * @param {HTTPRequestConfig} config Request Configuration.
   * @returns {HTTPPromise} Response Promise.
   */
  put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): HTTPPromise<T> {
    return this.httpClient.put(url, data, config);
  }

  /**
   * Send a Request.
   * @param {HTTPRequestConfig} config Request Configuration.
   * @returns {HTTPPromise} Response Promise.
   */
  patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): HTTPPromise<T> {
    return this.httpClient.patch(url, data, config);
  }

  protected prepareClient(httpClient: IHttpClient) {
    this.addRequestInterceptor(httpClient);
    this.addResponseInterceptor(httpClient);
  }

  protected addResponseInterceptor(httpClient: IHttpClient) {
    httpClient.interceptors.response.use(
      response => response,
      async error => {
        this.handleNotFoundStatusCode(error);
        this.handleForbiddenStatusCode(error);
        this.handleUnauthorizedStatusCode(error);

        return Promise.reject(error && error.response);
      }
    );
  }

  private async handleUnauthorizedStatusCode(error: any): Promise<void> {
    const refreshToken = Auth.getRefreshToken();
    const originalRequest = error.config;

    if (
      error?.response?.status === 401 &&
      (!refreshToken || originalRequest?.url === REFRESH_TOKEN)
    ) {
      if (!LOGIN_ROUTES.includes(router?.currentRoute?.name || '')) {
        this.logout();
      }
    }

    if (error?.response?.status === 401 && refreshToken) {
      await this.refreshAuthToken(refreshToken);
    }
  }

  private logout() {
    Auth.destroyToken();
    router.push({ name: LOGIN_ROUTE }).catch(e => e);
  }

  private handleNotFoundStatusCode(error: any): void {
    if (
      error?.response?.status === 404 &&
      router?.currentRoute?.name &&
      [AllReqPageName, PendingReqPageName].includes(router?.currentRoute?.name)
    ) {
      router.push({ name: 'Page404' }).catch(e => e);
    }
  }

  private handleForbiddenStatusCode(error: any): void {
    if (
      error?.response?.status === 403 &&
      !LOGIN_ROUTES?.includes(router?.currentRoute?.name || '')
    ) {
      window.location.href = '/dashboard';
    }
  }

  private async refreshAuthToken(refreshToken: string): Promise<void> {
    try {
      await AuthService.refreshAuthToken(refreshToken);
      window.location.href = '/dashboard';
    } catch {
      this.logout();
    }
  }

  protected addRequestInterceptor(httpClient: IHttpClient) {
    httpClient.interceptors.request.use(
      request => {
        const authToken = Auth.getToken();
        const isSameOrigin = request.url && !request.url.startsWith('http');

        if (isSameOrigin && authToken) {
          request.headers.Authorization = `Bearer ${authToken}`;
        } else {
          delete request.headers.Authorization;
        }

        return request;
      },
      err => Promise.reject(err)
    );
  }
}

/**
 * --------------------------------------------------
 *	HTTP Request Configuration Interface
 * --------------------------------------------------
 **/
export interface HTTPRequestConfig extends AxiosRequestConfig {
  //
}

/**
 * --------------------------------------------------
 *	HTTP Promise Interface
 * --------------------------------------------------
 **/
export interface HTTPPromise<T = any> extends AxiosPromise<T> {
  //
}

export const HTTPServiceClient: HttpService = new HttpService(HttpClient);
